import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Input } from 'reactstrap'
import SimpleBar from 'simplebar-react'
import { apiClient } from '../../../api/client'
import { APPLICATION_SETTINGS, DETAIL_SEARCH_TYPE } from '../../../common/constants'
import { clearDetailSearchCriteria, clearQuickSearchCriteria, setQuickSearchCriteria } from '../../../store/search'
import { selectQuickSearchCriteria } from '../../../store/search/selectors'
import Loader from '../Loader'
import SearchResult from './SearchResult/SearchResult'

const fetchQuickSearchData = (url) => apiClient(url).then((response) => response.data)

const QuickSearchInput = ({ mobileSearchValue, setMobileSearchValue, mobileSearchClose, hideDesktopInput = false }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const queryClient = useQueryClient()

    const [desktopSearchValue, setDesktopSearchValue] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

    const value = mobileSearchValue !== undefined ? mobileSearchValue : desktopSearchValue

    const setValue = (newValue) => {
        if (setMobileSearchValue) {
            setMobileSearchValue(newValue)
        } else {
            setDesktopSearchValue(newValue)
        }
    }

    const searchCriteria = useSelector(selectQuickSearchCriteria)
    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    let searchObject = {
        pageType: '',
        searchProps: { search: null },
    }

    const enableSearch = (searchQuery, searchArray) => {
        return queryClient.getQueryCache().find({ queryKey: [searchQuery, ...searchArray] }) === undefined && value !== ''
    }

    const findByResult = (searchValues) => {
        const searchArray = searchValues.split(',').map((item) => item.trim())

        const baseSearch = searchArray.length > 0 ? encodeURI(`&criteria[search]=${searchArray.join(',')}`) : ''
        searchObject.searchProps.search = searchArray.join(',')

        return [
            {
                queryKey: ['property', ...searchArray],
                queryFn: () => baseSearch && fetchQuickSearchData(`v1/property?limit=5${baseSearch}`),
                enabled: enableSearch('property', searchArray),
            },
            {
                queryKey: ['project', ...searchArray],
                queryFn: () => baseSearch && fetchQuickSearchData(`v1/project?limit=5${baseSearch}`),
                enabled: enableSearch('project', searchArray),
            },
            {
                queryKey: ['contactCompany', ...searchArray],
                queryFn: () => baseSearch && fetchQuickSearchData(`v1/contact?limit=5${baseSearch}&criteria[contactType]=company`),
                enabled: enableSearch('contactCompany', searchArray),
            },
            {
                queryKey: ['contactPerson', ...searchArray],
                queryFn: () => baseSearch && fetchQuickSearchData(`v1/contact?limit=5${baseSearch}&criteria[contactType]=person`),
                enabled: enableSearch('contactPerson', searchArray),
            },
        ]
    }

    const [property, project, contactCompany, contactPerson] = useQueries({
        queries: debouncedSearchTerm ? [...findByResult(debouncedSearchTerm)] : [],
    })

    const isFetching = [property, project, contactCompany, contactPerson].some((query) => query?.isFetching)

    const noResult =
        debouncedSearchTerm !== '' &&
        [property, project, contactCompany, contactPerson].every(
            (query) => query?.data === undefined || query?.data === null || query?.data === '' || query?.data?.count === 0
        )

    const handleShowAll = (pageType) => {
        searchObject.pageType = pageType
        const isProperty = pageType === 'property'

        dispatch(isProperty ? clearDetailSearchCriteria(DETAIL_SEARCH_TYPE.PROPERTY) : clearDetailSearchCriteria(DETAIL_SEARCH_TYPE.PROJECT))
        dispatch(setQuickSearchCriteria(searchObject))

        if (mobileSearchClose) {
            mobileSearchClose()
        }
    }

    const handleResultItemClick = () => {
        dispatch(setQuickSearchCriteria(searchObject))

        if (mobileSearchClose) {
            mobileSearchClose()
        }
    }

    useEffect(() => {
        const delay = 1000
        const timeoutId = setTimeout(() => setDebouncedSearchTerm(value), delay)
        return () => clearTimeout(timeoutId)
    }, [value])

    useEffect(() => {
        if (!searchCriteria) {
            setValue('')
        }
    }, [searchCriteria])

    useEffect(() => {
        var searchOptions = document.getElementById('search-close-options')
        var dropdown = document.getElementById('search-dropdown')
        var searchInput = document.getElementById('search-options')
        const navLinksArray = [...document.getElementsByClassName('nav-link')]
        const detailSearchButton = [...document.getElementsByClassName('detail-search-button')]

        navLinksArray.map((item) =>
            item.addEventListener('click', () => {
                searchCriteria && dispatch(clearQuickSearchCriteria())
                setValue('')
            })
        )

        detailSearchButton[0]?.addEventListener('click', () => {
            setValue('')
        })

        searchInput.addEventListener('focus', function () {
            var inputLength = searchInput.value.length
            if (inputLength > 0) {
                dropdown.classList.add('show')
            } else {
                dropdown.classList.remove('show')
            }
        })

        searchInput.addEventListener('keyup', function () {
            var inputLength = searchInput.value.length
            if (inputLength > 0) {
                dropdown.classList.add('show')
            } else {
                dropdown.classList.remove('show')
            }
        })

        searchOptions.addEventListener('click', function () {
            searchInput.value = ''
            dropdown.classList.remove('show')
            dispatch(clearQuickSearchCriteria())
            setValue('')
        })

        document.body.addEventListener('click', function (e) {
            if (e.target.getAttribute('id') !== 'search-options') {
                dropdown.classList.remove('show')
            }
        })
    }, [dispatch])

    if (hideDesktopInput && !value) {
        return null
    }

    return (
        <div className={hideDesktopInput ? 'mt-3' : 'app-search d-none d-lg-block'}>
            {!hideDesktopInput && (
                <div className="position-relative">
                    <Input
                        type="text"
                        className="form-control"
                        placeholder={`${t('quickSearch.placeholder')}...`}
                        id="search-options"
                        value={value}
                        onChange={(e) => {
                            setValue(e.target.value)
                        }}
                    />
                    <span className="mdi mdi-magnify search-widget-icon"></span>
                    <span
                        className={`mdi mdi-close-circle search-widget-icon search-widget-icon-close ${!value ? 'd-none' : ''}`}
                        id="search-close-options"
                    ></span>
                </div>
            )}
            <div className={hideDesktopInput ? '' : 'dropdown-menu dmn-search-dropdown-menu'} id={hideDesktopInput ? '' : 'search-dropdown'}>
                <SimpleBar style={{ height: 'auto', maxHeight: '320px' }}>
                    {isFetching ? (
                        <div className="p-1">
                            <Loader />
                        </div>
                    ) : noResult ? (
                        <div className="notification-list">
                            <div className="d-flex">
                                <div className="flex-1 text-center">{t('app.common.noResult')}</div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <SearchResult
                                linkName={'properties'}
                                searchResult={property}
                                handleShowAll={() => handleShowAll(DETAIL_SEARCH_TYPE.PROPERTY)}
                                onItemClick={handleResultItemClick}
                                currency={currency}
                                areaUnit={areaUnit}
                            />
                            <SearchResult
                                linkName={'projects'}
                                searchResult={project}
                                handleShowAll={() => handleShowAll(DETAIL_SEARCH_TYPE.PROJECT)}
                                onItemClick={handleResultItemClick}
                            />
                            <SearchResult
                                linkName={'contacts'}
                                searchResult={contactPerson}
                                handleShowAll={() => handleShowAll(DETAIL_SEARCH_TYPE.CONTACT)}
                                onItemClick={handleResultItemClick}
                            />
                            <SearchResult
                                linkName={'companies'}
                                searchResult={contactCompany}
                                handleShowAll={() => handleShowAll(DETAIL_SEARCH_TYPE.COMPANY)}
                                onItemClick={handleResultItemClick}
                            />
                        </>
                    )}
                </SimpleBar>
            </div>
        </div>
    )
}

export default QuickSearchInput
